import React from 'react';
interface InvoiceHeaderAltProps {
  company: {
    name: string;
    address: string[];
    uen: string;
    gst: string;
    travelLicense?: string;
    logoUrl?: string;
  };
  invoice: {
    number: string;
    issueDate: string;
    bookingRef: string;
    dueDate: string;
    salesStaff: string;
    bookingStaff: string;
    productName: string;
    printedDate: string;
    lastUpdateDate: string;
  };
  billTo: {
    name: string;
    address: string[];
    attn?: string;
  };
  pageNumber?: number;
  totalPages?: number;
  isFirstPage?: boolean;
}
export function InvoiceHeaderAlt({
  company,
  invoice,
  billTo,
  pageNumber = 1,
  totalPages = 1,
  isFirstPage = true
}: InvoiceHeaderAltProps) {
  return <>
      {/* Top Row: Logo Left, Address Right */}
      <div className="flex justify-between items-start mb-4" data-id="element-514">
        {/* Left: Logo */}
        <div data-id="element-515">
          {company.logoUrl ? <img src={company.logoUrl} alt={company.name} className="h-14 object-contain object-left" data-id="element-516" /> : <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md" data-id="element-517">
              <span className="text-white font-bold text-lg" data-id="element-518">PA</span>
            </div>}
        </div>

        {/* Right: Company Address - Compact */}
        <div className="text-right" data-id="element-519">
          <h1 className="text-sm font-bold text-neutral-900 mb-1" data-id="element-520">
            {company.name}
          </h1>
          <div className="text-[10px] text-neutral-500 space-y-0.5 leading-relaxed" data-id="element-521">
            {company.address.map((line, i) => <p key={i} data-id="element-522">{line}</p>)}
            <p className="mt-1" data-id="element-523">
              UEN: {company.uen} | GST: {company.gst}
            </p>
            {company.travelLicense && <p data-id="element-524">Travel Agent License: {company.travelLicense}</p>}
          </div>
        </div>
      </div>

      {/* Full-Width TAX INVOICE Section - Two Columns */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-4" data-id="element-525">
        <h2 className="text-lg font-bold text-neutral-900 mb-3 tracking-tight" data-id="element-526">
          TAX INVOICE
        </h2>

        {/* Two Column Grid - Reorganized */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5" data-id="element-527">
          {/* Left Column */}
          <div className="space-y-1.5" data-id="element-528">
            <div className="flex justify-between" data-id="element-529">
              <span className="text-xs font-medium text-neutral-600" data-id="element-530">
                Invoice No.:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-531">
                {invoice.number}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-532">
              <span className="text-xs font-medium text-neutral-600" data-id="element-533">
                Issue Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-534">
                {invoice.issueDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-535">
              <span className="text-xs font-medium text-neutral-600" data-id="element-536">
                Booking Ref:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-537">
                {invoice.bookingRef}
              </span>
            </div>
            {isFirstPage && <div className="flex justify-between" data-id="element-538">
                <span className="text-xs font-medium text-neutral-600" data-id="element-539">
                  Product Name:
                </span>
                <span className="text-xs text-neutral-900" data-id="element-540">
                  {invoice.productName}
                </span>
              </div>}
          </div>

          {/* Right Column */}
          <div className="space-y-1.5" data-id="element-541">
            <div className="flex justify-between" data-id="element-542">
              <span className="text-xs font-medium text-neutral-600" data-id="element-543">
                Due Date:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-544">
                {invoice.dueDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-545">
              <span className="text-xs font-medium text-neutral-600" data-id="element-546">
                Printed Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-547">
                {invoice.printedDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-548">
              <span className="text-xs font-medium text-neutral-600" data-id="element-549">
                Last Update:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-550">
                {invoice.lastUpdateDate}
              </span>
            </div>
            {isFirstPage && <>
                <div className="flex justify-between" data-id="element-551">
                  <span className="text-xs font-medium text-neutral-600" data-id="element-552">
                    Sales Staff:
                  </span>
                  <span className="text-xs font-semibold text-neutral-900" data-id="element-553">
                    {invoice.salesStaff}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-554">
                  <span className="text-xs font-medium text-neutral-600" data-id="element-555">
                    Booking Staff:
                  </span>
                  <span className="text-xs font-semibold text-neutral-900" data-id="element-556">
                    {invoice.bookingStaff}
                  </span>
                </div>
              </>}
          </div>
        </div>
      </div>

      {/* Bill To Section - Only on first page */}
      {isFirstPage && <div className="mb-6 bg-neutral-50 border border-neutral-200 rounded-lg p-4" data-id="element-557">
          <p className="text-sm font-bold text-neutral-900 mb-2 uppercase tracking-wide" data-id="element-558">
            Bill To:
          </p>
          <div className="space-y-1" data-id="element-559">
            <p className="text-base font-semibold text-neutral-900" data-id="element-560">
              {billTo.name}
            </p>
            <div className="text-sm text-neutral-700 space-y-0.5" data-id="element-561">
              {billTo.address.map((line, i) => <p key={i} data-id="element-562">{line}</p>)}
            </div>
            {billTo.attn && <p className="text-sm text-neutral-700 mt-2" data-id="element-563">
                Attn: {billTo.attn}
              </p>}
          </div>
        </div>}
    </>;
}